import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Sun, Moon, Monitor, Languages, User, Mail, Calendar, Link2, Unlink, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const Settings = () => {
  const { t, theme, setTheme, language, setLanguage, user } = useApp();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const [eventConfig, setEventConfig] = useState(null);
  const [eventLoading, setEventLoading] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [crmApiKey, setCrmApiKey] = useState('');
  const [crmUrl, setCrmUrl] = useState('');
  const [eventId, setEventId] = useState('');
  const [eventName, setEventName] = useState('');

  const token = localStorage.getItem('token');
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const fetchEventConfig = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/event-config/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setEventConfig(await res.json());
      } else {
        setEventConfig(null);
      }
    } catch (err) {
      console.error('Failed to fetch event config:', err);
    }
  }, [token]);

  useEffect(() => { fetchEventConfig(); }, [fetchEventConfig]);

  const handleConnectEvent = async () => {
    if (!crmApiKey || !crmUrl || !eventId) return;
    setEventLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/event-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ crm_api_key: crmApiKey, crm_url: crmUrl, event_id: eventId, event_name: eventName || undefined })
      });
      if (res.ok) {
        await fetchEventConfig();
        setShowEventForm(false);
        setCrmApiKey('');
      }
    } catch (err) {
      console.error('Connect failed:', err);
    }
    setEventLoading(false);
  };

  const handleDisconnectEvent = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/event-config`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setEventConfig(null);
    } catch (err) {
      console.error('Disconnect failed:', err);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) return;
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setChangingPassword(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      if (res.ok) {
        toast.success('Password changed successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to change password');
      }
    } catch (err) {
      toast.error('Failed to change password');
    }
    setChangingPassword(false);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight" data-testid="settings-title">
            {t('settings.title')}
          </h1>
          <p className="text-muted-foreground mt-1">{t('settings.subtitle')}</p>
        </div>

        <div className="space-y-6">
          {/* Appearance Settings */}
          <Card className="rounded-2xl border-slate-200 dark:border-slate-800" data-testid="appearance-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sun className="w-5 h-5" />
                {t('settings.appearance.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">{t('settings.appearance.theme')}</Label>
                  <p className="text-sm text-muted-foreground">
                    Choose your preferred theme
                  </p>
                </div>
                <Select value={theme} onValueChange={setTheme}>
                  <SelectTrigger className="w-40" data-testid="theme-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">
                      <div className="flex items-center gap-2">
                        <Sun className="w-4 h-4" />
                        {t('settings.appearance.light')}
                      </div>
                    </SelectItem>
                    <SelectItem value="dark">
                      <div className="flex items-center gap-2">
                        <Moon className="w-4 h-4" />
                        {t('settings.appearance.dark')}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Language Settings */}
          <Card className="rounded-2xl border-slate-200 dark:border-slate-800" data-testid="language-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Languages className="w-5 h-5" />
                {t('settings.language.title')}
              </CardTitle>
              <CardDescription>{t('settings.language.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="w-full" data-testid="language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">
                    <div className="flex items-center gap-2">
                      <span>🇬🇧</span>
                      English
                    </div>
                  </SelectItem>
                  <SelectItem value="pl">
                    <div className="flex items-center gap-2">
                      <span>🇵🇱</span>
                      Polski
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Account Settings */}
          <Card className="rounded-2xl border-slate-200 dark:border-slate-800" data-testid="account-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                {t('settings.account.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-900/50">
                <div className="w-12 h-12 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                  <User className="w-6 h-6 text-violet-600 dark:text-violet-400" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold">{user?.name || 'User'}</p>
                  <p className="text-sm text-muted-foreground">{user?.email || 'user@example.com'}</p>
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="w-4 h-4" />
                    {t('settings.account.email')}
                  </div>
                  <span className="text-sm">{user?.email || 'user@example.com'}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    {t('settings.account.memberSince')}
                  </div>
                  <span className="text-sm">
                    {user?.created_at ? format(new Date(user.created_at), 'PP') : 'N/A'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card className="rounded-2xl border-slate-200 dark:border-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Change Password
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Current Password</Label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min. 6 characters"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Confirm New Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat new password"
                />
              </div>
              <Button
                onClick={handleChangePassword}
                disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                className="w-full bg-violet-600 hover:bg-violet-700"
              >
                {changingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                Change Password
              </Button>
            </CardContent>
          </Card>

          {/* Event Mode */}
          <Card className="rounded-2xl border-slate-200 dark:border-slate-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="w-5 h-5" />
                  Event Mode
                </CardTitle>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${eventConfig?.active ? 'bg-green-500' : 'bg-gray-400'}`} />
                  <span className={`text-xs ${eventConfig?.active ? 'text-green-600' : 'text-gray-500'}`}>
                    {eventConfig?.active ? 'Connected' : 'Inactive'}
                  </span>
                </div>
              </div>
              <CardDescription>
                Connect to Sorto CRM to sync meeting data and contacts from events.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {eventConfig?.active ? (
                <>
                  <div className="p-4 rounded-xl bg-green-50 dark:bg-green-900/20 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Event</span>
                      <span className="font-medium">{eventConfig.event_name || eventConfig.event_id}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Event ID</span>
                      <span className="font-mono text-xs">{eventConfig.event_id}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">API Key</span>
                      <span className="font-mono text-xs">{eventConfig.crm_api_key}</span>
                    </div>
                  </div>
                  <Button variant="destructive" size="sm" className="w-full" onClick={handleDisconnectEvent}>
                    <Unlink className="w-4 h-4 mr-2" />
                    Disconnect Event
                  </Button>
                </>
              ) : showEventForm ? (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">CRM API Key *</Label>
                    <Input
                      type="password"
                      value={crmApiKey}
                      onChange={(e) => setCrmApiKey(e.target.value)}
                      placeholder="evt_... or mcp_..."
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">CRM URL *</Label>
                    <Input
                      value={crmUrl}
                      onChange={(e) => setCrmUrl(e.target.value)}
                      placeholder="https://…/crm/api/v1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Event ID *</Label>
                    <Input
                      value={eventId}
                      onChange={(e) => setEventId(e.target.value)}
                      placeholder="UUID from CRM"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Event Name</Label>
                    <Input
                      value={eventName}
                      onChange={(e) => setEventName(e.target.value)}
                      placeholder="Optional display name"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button className="flex-1" onClick={handleConnectEvent} disabled={eventLoading || !crmApiKey || !eventId}>
                      {eventLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                      Connect
                    </Button>
                    <Button variant="outline" onClick={() => setShowEventForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" className="w-full" onClick={() => setShowEventForm(true)}>
                  <Link2 className="w-4 h-4 mr-2" />
                  Setup Event Mode
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Settings;
